import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { first, map, retry, shareReplay, tap } from "rxjs/operators";
import {
  AbstractCirculationsService,
  Circulation,
  CirculationId,
  CirculationStatus,
  EntityId,
  IBaseMetaDataSettings,
  ICirculationInit,
  ICirculationPayload,
  ICirculationTemplate,
  ICirculationTemplateStep,
  IDocument,
  IDocumentUploadEndpoints,
  IHyperlinkToSave,
  IMetaData,
  IMyTask,
  IReorderStepsData,
  IStartMultipleCirculationsResult,
  IStepForwardData,
  IStepInit,
  IStepOverrideForwardData,
  IStepPayload,
  IStepResultData,
  IStepTasks,
  IUser,
  OutcomeStatus,
  prepareId,
  Step,
  StepId,
  StepResult,
  StepStatus
} from "../interfaces";
import { WebServiceResponse } from "../interfaces/helpTarget";
import { LogClass, Logger } from "../logger";

@LogClass({ source: "CirculationsService" })
@Injectable({
  providedIn: "root"
})
export class CirculationsService implements AbstractCirculationsService {
  private logger: Logger;
  private cachedHttp: SimpleCacheHttp;

  constructor(private http: HttpClient) {
    this.cachedHttp = new SimpleCacheHttp(1000, this.http);
    this.logger.debug("ctor");
  }

  public DownloadDocument(circId: EntityId, docId: string): Observable<Blob> {
    const url = `/api/Circulation/${circId}/Documents/${docId}`;
    return this.http
      .get(url, {
        responseType: "blob"
      })
      .pipe(retry(3));
  }

  public overrideForwardStep(data: IStepOverrideForwardData): Observable<void> {
    data.step = this.clearImgSrcFromStepInit(data.step);

    const url = "/api/Step/OverrideForward";
    return this.http
      .post<WebServiceResponse<void>>(url, data, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("overrideForwardStep"),
        map((res) => res.data)
      );
  }

  public openDocument(circId: EntityId, doc: IDocument): void {
    this.DownloadDocument(circId, doc.id).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    });
  }

  public ApplyTemplate(
    template: ICirculationTemplate
  ): Observable<CirculationId> {
    const url = "/api/Circulation/Template/" + template.id + "/Apply";
    return this.http
      .post<WebServiceResponse<CirculationId>>(
        url,
        {},
        {
          responseType: "json"
        }
      )
      .pipe(
        this.logger.observableOperator("ApplyTemplate"),
        map((res) => res.data)
      );
  }

  public CancelCirculation(circulation: Circulation): Observable<void> {
    const url = "/api/Circulation/Cancel";
    return this.http
      .post<WebServiceResponse<void>>(url, this.clearImgSrcFromCirculationInit(circulation.props), {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("CancelCirculation"),
        map((res) => res.data)
      );
  }

  public DeleteTemplate(template: ICirculationTemplate): Observable<void> {
    const url = `/api/Circulation/Template/${template.id}`;
    this.logger.info("deleting Template");
    return this.http
      .delete<WebServiceResponse<void>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("DeleteTemplate"),
        map((res) => res.data)
      );
  }

  public CompleteStep(step: Step, result: IStepResultData): Observable<void> {
    const url = "/api/Step/Complete";
    return this.http
      .post<WebServiceResponse<void>>(
        url,
        {
          step: this.clearImgSrcFromStepInit(step.props),
          result
        },
        {
          responseType: "json"
        }
      )
      .pipe(
        this.logger.observableOperator("CompleteStep"),
        map((res) => res.data)
      );
  }

  public CreateNewFromPrepared(id: prepareId): Observable<CirculationId> {
    const url = "/api/Circulation/Templates/ApplyPrepared/" + id;
    return this.http
      .post<WebServiceResponse<CirculationId>>(url, null, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("CreateNewFromPrepared"),
        map((res) => res.data)
      );
  }

  public DeleteStep(step: IStepInit): Observable<CirculationId> {
    step = this.clearImgSrcFromStepInit(step);
    const url = "/api/Step/Delete";
    return this.http
      .post<WebServiceResponse<CirculationId>>(url, step, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("DeleteStep"),
        map((res) => res.data)
      );
  }

  public ForwardStep(data: IStepForwardData): Observable<void> {
    data.step = this.clearImgSrcFromStepInit(data.step);
    const url = "/api/Step/Forward";
    return this.http
      .post<WebServiceResponse<void>>(url, data, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("ForwardStep"),
        map((res) => res.data)
      );
  }

  public GetCirculation(id: string): Observable<Circulation> {
    const url = "/api/Circulation/" + id;
    return this.cachedHttp
      .get<WebServiceResponse<ICirculationInitResponse>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetCirculation"),
        map((x) => {
          return new Circulation(this.convertCirculationInit(x.data));
        })
      );
  }

  public GetCirculations(onlyRuning: boolean): Observable<Circulation[]> {
    let url = "/api/Circulation";
    if (!onlyRuning) {
      url += "/My";
    }
    return this.cachedHttp
      .get<WebServiceResponse<ICirculationInitResponse[]>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetMyCirculations"),
        map((x) => {
          return x.data.map(
            (y) => new Circulation(this.convertCirculationInit(y))
          );
        })
      );
  }

  public AddHyperlink(
    doc: IHyperlinkToSave,
    circId: CirculationId,
    stepNo?: number
  ): Observable<IDocument> {
    const url =
      "/api/Circulation/" + circId + "/Documents/hyperlink/" + (stepNo || 0);
    return this.http
      .post<WebServiceResponse<IDocument>>(url, doc, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("AddHyperlink"),
        map((res) => res.data)
      );
  }

  public GetDocuments(id: CirculationId): Observable<IDocument[]> {
    const url = "/api/Circulation/" + id + "/Documents";
    return this.cachedHttp
      .get<WebServiceResponse<IDocument[]>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetDocuments"),
        map((res) => {
          return res.data.map((x: IDocument) => {
            return {
              ...x,
              uri: encodeURI(x.uri).replace("#", "%23")
            };
          });
        })
      );
  }

  public GetDocumentUploadEndpoints(
    id: CirculationId,
    stepNo: number
  ): Observable<IDocumentUploadEndpoints> {
    return of({
      removeUrl: `/unused_see_removeDocument_method`,
      saveUrl: `/api/Circulation/${id}/Documents/${stepNo}`,
      restrictions: {
        allowedExtensions: [],
        maxFileSize: 52428800,
        minFileSize: 1
      } //50MB
    }).pipe(this.logger.observableOperator("GetDocumentUploadEndpoints"));
  }

  public GetMyTasks(): Observable<IMyTask[]> {
    const url = "/api/Step/My";
    return this.cachedHttp
      .get<WebServiceResponse<IMyTask[]>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetMyTasks"),
        map((s) => {
          const data = s.data;
          this.convertDates(data);
          return data;
        })
      );
  }

  private convertDates(object: unknown) {
    const utcDateRegex =
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)Z$/;
    if (!object || !(object instanceof Object)) {
      return;
    }

    if (object instanceof Array) {
      for (const item of object) {
        this.convertDates(item);
      }
    }

    for (const key of Object.keys(object)) {
      const value = object[key];

      if (value instanceof Array) {
        for (const item of value) {
          this.convertDates(item);
        }
      }

      if (value instanceof Object) {
        this.convertDates(value);
      }

      if (typeof value === "string" && utcDateRegex.test(value)) {
        object[key] = new Date(value);
      }
    }
  }
  public GetMyTemplates(): Observable<ICirculationTemplate[]> {
    const url = "/api/Circulation/Templates/My";
    return this.cachedHttp
      .get<WebServiceResponse<ICirculationTemplate[]>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetMyTemplates"),
        map((res) => res.data)
      );
  }

  public GetStep(cId: CirculationId, sId: StepId): Observable<Step> {
    return this.GetSteps(cId).pipe(
      this.logger.observableOperator("GetStep"),
      map((s) => {
        // tslint:disable-next-line: triple-equals
        const step = s.find((x) => x.id == sId);
        if (!step) {
          throw new Error("step not found.");
        }

        return step;
      })
    );
  }

  public GetSteps(id: CirculationId): Observable<Step[]> {
    const url = "/api/Step/" + id;
    return this.http
      .get<WebServiceResponse<IStepInitResponse[]>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetSteps"),
        map((s) => {
          return s.data.map((x) => new Step(this.convertStepInit(x), x.id));
        })
      );
  }

  public GetTemplate(id: CirculationId): Observable<ICirculationTemplate> {
    const url = "/api/Circulation/Template/" + id;
    return this.cachedHttp
      .get<WebServiceResponse<ICirculationTemplate>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetTemplate"),
        map((res) => res.data)
      );
  }

  public GetTemplates(): Observable<ICirculationTemplate[]> {
    const url = "/api/Circulation/Templates";
    return this.cachedHttp
      .get<WebServiceResponse<ICirculationTemplate[]>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("GetTemplates"),
        map((res) => res.data)
      );
  }

  public RemoveDocument(
    circId: CirculationId,
    docId: string
  ): Observable<void> {
    const url = `/api/Circulation/${circId}/Documents/${docId}`;
    return this.http
      .delete<WebServiceResponse<void>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("RemoveDocument"),
        map((res) => res.data)
      );
  }

  public ReorderSteps(
    reorderData: IReorderStepsData
  ): Observable<CirculationId> {
    const url = "/api/Step/Reorder";
    return this.http
      .post<WebServiceResponse<CirculationId>>(url, reorderData, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("ReorderSteps"),
        map((res) => res.data)
      );
  }

  public SaveCirculation(circulation: Circulation): Observable<CirculationId> {
    const url = "/api/Circulation";
    return this.http
      .post<WebServiceResponse<CirculationId>>(url, this.clearImgSrcFromCirculationInit(circulation.props), {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("SaveCirculation"),
        map((res) => res.data)
      );
  }

  public CopyCirculation(circulationId: EntityId): Observable<CirculationId> {
    const url = `/api/Circulation/Reactivate/${circulationId}`;
    return this.http
      .post<WebServiceResponse<CirculationId>>(url, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("CopyCirculation"),
        map((res) => res.data)
      );
  }
  public SaveCirculationTemplate(
    template: ICirculationTemplate
  ): Observable<CirculationId> {
    const url = "/api/Circulation/Template";
    return this.http
      .post<WebServiceResponse<CirculationId>>(url, this.clearImgSrcFromCirculationTemplate(template), {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("SaveCirculationTemplate"),
        map((res) => res.data)
      );
  }

  public SaveStep(step: Step): Observable<StepId> {
    const url = "/api/Step";
    return this.http
      .post<WebServiceResponse<StepId>>(url, this.clearImgSrcFromStepInit(step.props), {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("SaveStep"),
        map((res) => res.data)
      );
  }

  public DeleteTemplateStep(
    step: ICirculationTemplateStep,
    template: ICirculationTemplate
  ): Observable<void> {
    const currentSteps = template.steps;
    template.steps = [];
    currentSteps.forEach((item) => {
      if (item.stepNo === step.stepNo) {
        return;
      }

      if (item.stepNo < step.stepNo) {
        item.stepNo = item.stepNo - 1;
      }

      template.steps.push(item);
    });

    return this.SaveCirculationTemplate(template).pipe(
      tap(() => this.logger.observableOperator("DeleteTemplateStep")),
      map(() => {
        return;
      })
    );
  }

  public DeleteTemplateData(
    data: IMetaData,
    template: ICirculationTemplate
  ): Observable<void> {
    template.payload.data = template.payload.data.filter(
      (d) => d.key != data.key
    );

    return this.SaveCirculationTemplate(template).pipe(
      tap(() => this.logger.observableOperator("DeleteTemplateData")),
      map(() => {
        return;
      })
    );
  }

  public StartCirculation(circulation: Circulation): Observable<void> {
    const url = "/api/Circulation/Start";
    return this.http
      .post<WebServiceResponse<void>>(url, this.clearImgSrcFromCirculationInit(circulation.props), {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("StartCirculation"),
        map((res) => res.data)
      );
  }

  public StartMultipleCirculations(
    circulations: ICirculationInit[]
  ): Observable<IStartMultipleCirculationsResult> {
    const url = "/api/Circulation/StartMultiple";
    const data = {
      circulationIds: circulations.map((x) => x.id)
    };
    return this.http
      .post<WebServiceResponse<IStartMultipleCirculationsResult>>(url, data, {
        responseType: "json"
      })
      .pipe(
        this.logger.observableOperator("overrideForwardStep"),
        map((res) => res.data)
      );
  }

  private convertCirculationInit(
    wsResult: ICirculationInitResponse
  ): ICirculationInit {
    let dueDate: Date = null;
    if (wsResult.dueDate) {
      dueDate = new Date(wsResult.dueDate);
    }
    if (wsResult.payload?.data) {
      // ensure Properties here are all (!) camlCase (!)
      for (const data of wsResult.payload.data) {
        if (!data.settings) {
          continue;
        }

        const settings = {} as IBaseMetaDataSettings;
        for (const name of Object.getOwnPropertyNames(data.settings)) {
          if (!name.match(/[a-z]+/)) {
            settings[name] = data.settings[name];
            continue;
          }

          const camlCaseName =
            name.substring(0, 1).toLowerCase() + name.substring(1);
          settings[camlCaseName] = data.settings[name];
        }
        data.settings = settings;

        // date metadata should be date
        if (data.valueType == "date" && data.value) {
          data.value = new Date(data.value as string);
        }
      }
    }

    return {
      ...wsResult,
      dueDate: dueDate
    };
  }

  private convertStepInit(wsResult: IStepInitResponse): IStepInit {
    let dueDate: Date = null;
    if (wsResult.dueDate) {
      dueDate = new Date(wsResult.dueDate);
    }
    wsResult.tasks.assignees.forEach((a) => {
      if (!a.completionDate) {
        return;
      }
      a.completionDate = new Date(a.completionDate);
    });
    return {
      ...wsResult,
      dueDate: dueDate
    };
  }

  private clearImgSrcFromStepInit(input: IStepInit): IStepInit {
    input.tasks.assignees.forEach((a) => {
      a.user.imageSrc = "";
    });
    return input;
  }

  private clearImgSrcFromCirculationInit(input: ICirculationInit): ICirculationInit {
    input.owner.forEach((a) => {
      a.imageSrc = "";
    });

    input.payload.additionalMailRecipients.forEach((a) => {
      a.imageSrc = "";
    });

    return input;
  }

  private clearImgSrcFromCirculationTemplate(input: ICirculationTemplate): ICirculationTemplate {
    input.payload.additionalMailRecipients?.forEach((a) => {
      a.imageSrc = "";
    });
    input.steps.forEach((s) => {
      s.tasks.assignedTo.forEach((a) => {
        a.imageSrc = "";
      });
    });

    return input;
  }

}
// like ICirculationInit, but all dates are strings
interface ICirculationInitResponse {
  id: CirculationId;
  owner: IUser[];
  startDate: string;
  payload: ICirculationPayload;
  status: CirculationStatus;
  outcome?: OutcomeStatus;
  title: string;
  dueDate: string;
}

// like IStepInit, but all dates are strings
interface IStepInitResponse {
  tasks: IStepTasks;
  circulationId: CirculationId;
  dueDate: string;
  id: string;
  payload: IStepPayload;
  result?: StepResult;
  status: StepStatus;
  stepNo: number;
}

@LogClass({ source: "SimpleCacheHttp" })
export class SimpleCacheHttp {
  private logger: Logger;
  private cachedRequests: {
    [url: string]: Observable<unknown>;
  } = {};

  constructor(private cacheTimeoutMillis: number, private http: HttpClient) {
    this.logger.debug("init with timeout: " + cacheTimeoutMillis);
  }

  public get<T>(
    url: string,
    options?: {
      headers?:
      | HttpHeaders
      | {
        [header: string]: string | string[];
      };
      observe?: "body";
      params?:
      | HttpParams
      | {
        [param: string]: string | string[];
      };
      reportProgress?: boolean;
      responseType?: "json";
      withCredentials?: boolean;
    }
  ): Observable<T> {
    let replySubject = this.cachedRequests[url];
    const log = this.logger;
    if (!replySubject) {
      log.debug("caching for url:" + url);
      replySubject = this.http.get<T>(url, options).pipe(
        first(),
        tap(() => {
          log.debug("got response for url:" + url);
          window.setTimeout(() => {
            log.debug("clearing cache for url:" + url);
            this.cachedRequests[url] = null;
          }, this.cacheTimeoutMillis);
        }),
        shareReplay({
          bufferSize: 1,
          refCount: false
        })
      );
      this.cachedRequests[url] = replySubject;
    } else {
      log.debug("sharing response for url:" + url);
    }
    return replySubject as Observable<T>;
  }
}
